import { useEffect, useState } from "react";
import { Switch } from "../ui/switch";
import { NavigationArrow, WarningCircle, ShieldCheck } from "@phosphor-icons/react";
import { useLiveLocation } from "../../contexts/LiveLocationContext";

const ago = (ts) => {
  if (!ts) return "";
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  return s < 60 ? "just now" : `${Math.round(s / 60)} min ago`;
};

/** My Work card: engineer opt-in live location sharing. Stays on across pages and app reopens until switched off. */
export default function LiveLocationCard() {
  const { eligible, sharing, status, error, lastFix, start, stop } = useLiveLocation();
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(t); }, []);
  if (!eligible) return null;

  const tone = status === "error" ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" : sharing ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-border bg-card";
  return (
    <div className={`rounded-lg border p-4 ${tone}`} data-testid="live-location-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className={`mt-0.5 h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${sharing ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`}>
            <NavigationArrow size={18} weight={sharing ? "fill" : "regular"} className={sharing && status === "sharing" ? "animate-pulse" : ""} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold">Share my live location</p>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="live-location-status">
              {!sharing && "Off — the office only sees your site check-ins and home base."}
              {sharing && status === "locating" && "Getting a GPS fix…"}
              {sharing && status === "sharing" && lastFix && <>Sharing · sent {ago(lastFix.sentAt)}{lastFix.accuracy ? ` · ±${Math.round(lastFix.accuracy)} m` : ""}</>}
              {sharing && status === "error" && <span className="text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"><WarningCircle size={12} weight="fill" /> {error}</span>}
            </p>
          </div>
        </div>
        <Switch checked={sharing} onCheckedChange={(on) => (on ? start() : stop())} aria-label="Share my live location" data-testid="live-location-switch" />
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground inline-flex items-start gap-1.5" data-testid="live-location-privacy">
        <ShieldCheck size={13} className="shrink-0 mt-px" />
        <span>While it's on, the office sees where you are right now so urgent jobs go to whoever's closest. Updates roughly every minute or when you move; a position older than 15 minutes no longer counts as live. Switching off deletes your stored position.</span>
      </p>
    </div>
  );
}
